import { useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import useRegisterGlobalNudgesCallback, {
  NUDGE_TYPE_KEY
} from '~/modules/resource-management/resourceUserDetails/hooks/useRegisterGlobalNudgesCallback';
import { useResourceUserDetailsContext } from '~/modules/resource-management/resourceUserDetails/ResourceUserDetailsContext';
import { RESOURCE_USER_DETAILS_ACTION } from '~/modules/resource-management/resourceUserDetails/hooks/resourceUserDetailsAction';
import useUserSkillNudges from './useUserSkillNudges';
import { categorizeSkillSuggestions } from './categorizeSkillSuggestions';
import useOnAcceptUserNudges from './useOnAcceptUserNudges';
import useOnRejectUserNudges from './useOnRejectUserNudges';
import { useApplyUserDefinedSkillModificationsForUser } from './useApplyUserDefinedSkillModificationsForUser';
import useBulkRejectUserSkillNudge from './useBulkRejectUserSkillNudge';
import {
  transformUserDefinedSkillSuggestions,
  transformSystemSkillSuggestions
} from './skillTransformationUtils';

const EMPTY_ARRAY = [];

const extractNudgeIds = categorizedSuggestions => {
  if (!categorizedSuggestions) return [];

  const userDefinedSuggestions =
    categorizedSuggestions.userDefinedSkills || EMPTY_ARRAY;
  const systemLevelSkills =
    categorizedSuggestions.systemLevelSkills || EMPTY_ARRAY;

  return [
    ...userDefinedSuggestions.map(skill => skill.nudgeId),
    ...systemLevelSkills.map(skill => skill.nudgeId)
  ].filter(Boolean);
};

const filterExistingSkills = (nudges, resourceSkills, userDefinedSkills) => {
  if (!nudges.length) return EMPTY_ARRAY;

  const existingSkillNames = new Set();

  resourceSkills.forEach(skillGroup => {
    skillGroup.skillAssignments?.forEach(skill => {
      if (skill.displayText) {
        existingSkillNames.add(skill.displayText);
      }
    });
  });

  userDefinedSkills.forEach(skill => {
    if (skill.displayText) {
      existingSkillNames.add(skill.displayText);
    }
  });

  return nudges.filter(({ skillName }) => {
    if (!skillName) return true;

    return !existingSkillNames.has(skillName);
  });
};

export const useSkillNudgeActions = ({
  userUri,
  resourceSkills,
  userDefinedSkills = [],
  editable,
  canCreateSkills,
  isPsaPraaUserProfileNudgesEnabled
}) => {
  const {
    userSkillNudges: rawUserSkillNudges,
    isUserSkillNudgesLoading
  } = useUserSkillNudges({
    userUri,
    skip: !(isPsaPraaUserProfileNudgesEnabled && canCreateSkills)
  });
  const intl = useIntl();
  const { dispatch } = useResourceUserDetailsContext();

  const userSkillNudges = useMemo(
    () =>
      filterExistingSkills(
        rawUserSkillNudges,
        resourceSkills,
        userDefinedSkills
      ),
    [rawUserSkillNudges, resourceSkills, userDefinedSkills]
  );

  const categorizedSuggestions = useMemo(
    () => categorizeSkillSuggestions(userSkillNudges),
    [userSkillNudges]
  );

  const { onAcceptUserNudges } = useOnAcceptUserNudges({
    userUri,
    canCreateSkills
  });
  const { onRejectUserNudges } = useOnRejectUserNudges({ userUri });

  const { bulkRejectUserSkillNudge } = useBulkRejectUserSkillNudge({ userUri });

  const {
    applyUserDefinedSkillModificationsForUser
  } = useApplyUserDefinedSkillModificationsForUser({
    userId: userUri
  });

  const onAcceptUserDefinedSkills = useCallback(
    async ({ suggestions, skipSnackbar = false }) => {
      const { addSkills, nudgeIds } = transformUserDefinedSkillSuggestions(
        suggestions
      );

      await applyUserDefinedSkillModificationsForUser({
        addSkills,
        removeSkills: [],
        updateSkills: [],
        nudgeIds
      });

      if (!addSkills?.length || skipSnackbar) return;
      dispatch({
        type: RESOURCE_USER_DETAILS_ACTION.SET_SNACKBAR_MESSAGE,
        snackbarMessage: intl.formatMessage(
          { id: 'skillInfoCard.suggestionAccepted' },
          { skill: addSkills[0].skillName }
        )
      });
    },

    [intl, dispatch, applyUserDefinedSkillModificationsForUser]
  );

  const processSystemLevelSkills = useCallback(
    async systemLevelSkills => {
      if (!systemLevelSkills.length) return;

      const systemOnlySkills = transformSystemSkillSuggestions(
        systemLevelSkills
      );

      if (systemOnlySkills.length > 0) {
        await onAcceptUserNudges({ suggestions: systemOnlySkills });
      }
    },
    [onAcceptUserNudges]
  );

  const createAcceptAllSkillNudges = useCallback(
    async messageId => {
      if (!categorizedSuggestions || !userUri) return;

      const userDefinedSuggestions =
        categorizedSuggestions.userDefinedSkills || EMPTY_ARRAY;
      const systemLevelSkills =
        categorizedSuggestions.systemLevelSkills || EMPTY_ARRAY;

      const allNudgeIds = extractNudgeIds(categorizedSuggestions);

      if (!allNudgeIds.length) return;

      if (userDefinedSuggestions.length > 0) {
        const { userDefinedSkillData } = transformUserDefinedSkillSuggestions(
          userDefinedSuggestions
        );

        await onAcceptUserDefinedSkills({
          suggestions: userDefinedSkillData,
          skipSnackbar: true
        });
      }

      if (systemLevelSkills.length > 0) {
        await processSystemLevelSkills(systemLevelSkills);
      }

      dispatch({
        type: RESOURCE_USER_DETAILS_ACTION.SET_SNACKBAR_MESSAGE,
        snackbarMessage: intl.formatMessage({
          id: messageId
        })
      });
    },
    [
      intl,
      userUri,
      dispatch,
      categorizedSuggestions,
      onAcceptUserDefinedSkills,
      processSystemLevelSkills
    ]
  );

  const createRejectAllSkillNudges = useCallback(
    async messageId => {
      const allNudgeIds = extractNudgeIds(categorizedSuggestions);

      if (allNudgeIds.length > 0) {
        await bulkRejectUserSkillNudge(allNudgeIds);
      }
      dispatch({
        type: RESOURCE_USER_DETAILS_ACTION.SET_SNACKBAR_MESSAGE,
        snackbarMessage: intl.formatMessage({
          id: messageId
        })
      });
    },
    [intl, dispatch, categorizedSuggestions, bulkRejectUserSkillNudge]
  );

  const acceptAllSkillNudges = useCallback(
    () => createAcceptAllSkillNudges('skillInfoCard.allSuggestionsAccepted'),
    [createAcceptAllSkillNudges]
  );

  const rejectAllSkillNudges = useCallback(
    () => createRejectAllSkillNudges('skillInfoCard.allSuggestionsRejected'),
    [createRejectAllSkillNudges]
  );

  const globalAcceptCallback = useCallback(
    () => createAcceptAllSkillNudges('dela.acceptedAllSuggestions'),
    [createAcceptAllSkillNudges]
  );

  const globalRejectCallback = useCallback(
    () => createRejectAllSkillNudges('dela.rejectedAllSuggestions'),
    [createRejectAllSkillNudges]
  );

  const hasNudges = Boolean(
    categorizedSuggestions?.userDefinedSkills?.length ||
      categorizedSuggestions?.systemLevelSkills?.length
  );

  useRegisterGlobalNudgesCallback({
    nudgeTypeKey: NUDGE_TYPE_KEY.SKILL_NUDGES,
    editable: editable && canCreateSkills,
    onAccept: hasNudges && canCreateSkills ? globalAcceptCallback : null,
    onReject: hasNudges && canCreateSkills ? globalRejectCallback : null
  });

  return {
    isUserSkillNudgesLoading,
    categorizedSuggestions,
    userDefinedSkillSuggestions:
      categorizedSuggestions?.userDefinedSkills || EMPTY_ARRAY,
    systemLevelSkillSuggestions:
      categorizedSuggestions?.systemLevelSkills || EMPTY_ARRAY,
    onAcceptUserNudges,
    onRejectUserNudges,
    onAcceptUserDefinedSkills,
    acceptAllSkillNudges,
    rejectAllSkillNudges,
    hasNudges
  };
};

export default useSkillNudgeActions;
